import {
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { Code2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CodeColorBar } from './CodeColorBar';
import Editor, { Monaco, OnMount, BeforeMount } from '@monaco-editor/react';
import { useTheme } from '@/contexts/ThemeContext';
import { getOpenSCADCompletionProvider } from '@/lib/openscad-completions';
import { useScadParser } from '@/hooks/useScadParser';
import { ScadFeatureDialog } from '@/components/code/ScadFeatureDialog';
import {
  ExtractToFileDialog,
  type ExtractToFilePayload,
} from '@/components/code/ExtractToFileDialog';
import {
  ExtractToVariableDialog,
  type ExtractToVariableResult,
} from '@/components/code/ExtractToVariableDialog';
import {
  RENDER_SETTINGS,
  PRIMITIVES,
  TRANSFORMS,
  EXTRUDE_OPS,
  LIGHTS,
  LIBRARY_FEATURES,
  CLONE_OPS,
  SCENE,
  CAMERAS,
  SCENE_GRIDS,
  SCENE_CAMERA,
  SVG_OVERLAY,
  INTERACTION_FEATURES,
  MOTION_FEATURES,
  MESH_CLONE,
  MATERIAL_DEF,
  SHADER_MATERIAL,
  JS_SVG,
  PARAMETER_FEATURES,
} from '@/lib/scad-constants';
import { ScadFeature } from '@/lib/scad-feature-types';
import { findFeatureInvocationFull, parseScadArgs } from '@/lib/scad-utils';
import { stripMeshpitCode } from '@/lib/scad-preprocessor';
import {
  detectLiteralType,
  suggestVariableNameForLiteral,
  countOtherLiteralOccurrences,
  parseOuterVariables,
  findInsertionLineForVariable,
  applyExtractToVariable,
  type DetectedLiteral,
} from '@/utils/scadExtractUtils';
import {
  DARK_BG,
  DARK_FG,
  DARK_LINE_HIGHLIGHT,
  DARK_LINE_NUMBER,
  DARK_SELECTION,
  DARK_SELECTION_INACTIVE,
  DARK_BRACKET_HIGHLIGHT,
  DARK_COMMENT,
  DARK_KEYWORD,
  DARK_IDENTIFIER,
  DARK_STRING,
  DARK_NUMBER,
  DARK_DELIMITER,
  DARK_OPERATOR,
  DARK_BRACKET,
  LIGHT_BG,
  LIGHT_FG,
  LIGHT_LINE_HIGHLIGHT,
  LIGHT_LINE_NUMBER,
  LIGHT_SELECTION,
  LIGHT_SELECTION_INACTIVE,
  LIGHT_COMMENT,
  LIGHT_KEYWORD,
  LIGHT_IDENTIFIER,
  LIGHT_STRING,
  LIGHT_NUMBER,
  LIGHT_DELIMITER,
  LIGHT_OPERATOR,
} from '@/lib/syntaxTheme';

// Monaco token rules require the hex color without the leading '#'
const hex = (color: string) => color.replace(/^#/, '');

// Inject the CSS for clickable feature decorations exactly once per page load.
const EDITABLE_CLASS = 'scad-editable-feature';
if (!document.getElementById('scad-editable-feature-style')) {
  const style = document.createElement('style');
  style.id = 'scad-editable-feature-style';
  style.textContent = `
    .${EDITABLE_CLASS} {
      cursor: pointer;
      border-bottom: 1px dashed currentColor;
      opacity: 0.8;
    }
    .${EDITABLE_CLASS}:hover {
      opacity: 1;
      border-bottom-style: solid;
    }
  `;
  document.head.appendChild(style);
}

// Only features that have fields (i.e. open a dialog) are included.
function buildFeatureMap(
  groups: Record<string, ScadFeature>[],
): Record<string, ScadFeature> {
  const map: Record<string, ScadFeature> = {};
  for (const group of groups) {
    for (const feat of Object.values(group)) {
      if (!feat.fields || feat.fields.length === 0) continue;
      // Prefer the feature id as the lookup key first (e.g. "cube", "translate")
      map[feat.id] = feat;
      // Also derive function name from code/before string in case it differs
      const codeSnippet = feat.code ?? feat.before ?? '';
      const match = codeSnippet.match(/^([a-zA-Z_$][\w$]*)/);
      if (match && match[1] !== feat.id) {
        map[match[1]] = feat;
      }
    }
  }
  return map;
}

const ALL_FEATURES: Record<string, ScadFeature> = buildFeatureMap([
  PRIMITIVES,
  TRANSFORMS,
  EXTRUDE_OPS,
  LIGHTS,
  CLONE_OPS,
  SCENE,
  CAMERAS,
  SCENE_GRIDS,
  SCENE_CAMERA,
  SVG_OVERLAY,
  INTERACTION_FEATURES,
  MOTION_FEATURES,
  MESH_CLONE,
  MATERIAL_DEF,
  SHADER_MATERIAL,
  JS_SVG,
  RENDER_SETTINGS,
  PARAMETER_FEATURES,
]);

// Add library features (keyed by first word of their code snippet)
Object.values(LIBRARY_FEATURES).forEach((featureList) => {
  featureList.forEach((feat) => {
    if (!feat.fields || feat.fields.length === 0) return;
    const match = feat.code?.match(/^([a-zA-Z_$][\w$]*)/);
    if (match) {
      ALL_FEATURES[match[1]] = feat as ScadFeature;
    }
    if (feat.id && !ALL_FEATURES[feat.id]) {
      ALL_FEATURES[feat.id] = feat as ScadFeature;
    }
  });
});

export interface CodeEditorRef {
  editor: unknown;
  monaco: Monaco | null;
  insertText: (text: string) => void;
  wrapSelection: (before: string, after: string) => void;
  getSelectedText: () => string | null;
  hasSelection: () => boolean;
  undo: () => void;
  redo: () => void;
  setValueWithUndo: (value: string) => void;
  getValue: () => string;
  insertAtTop: (text: string) => void;
  /** Appends text at the very end of the document, preceded by a newline. */
  insertAtEnd: (text: string) => void;
  /**
   * Finds the first occurrence of `search` (string or RegExp) in the document
   * and replaces it with `replacement`. The edit is undoable.
   */
  replaceText: (search: string | RegExp, replacement: string) => void;
}

interface CodeEditorProps {
  value: string;
  onChange: (value: string | undefined) => void;
  onExtractToFile?: (payload: ExtractToFilePayload) => void;
  onExtractModulesToFiles?: () => void;
  onExplainWithAi?: (selectedCode: string, contextCode: string) => void;
  onImproveWithAi?: (
    selectedCode: string,
    contextCode: string,
    selectionRange: {
      startLineNumber: number;
      startColumn: number;
      endLineNumber: number;
      endColumn: number;
    },
  ) => void;
  onImproveParamsWithAi?: (contextCode: string) => void;
  language?: string;
  filePath?: string;
  /**
   * Map of 1-based line numbers → RGBA colour for every `color()` call that
   * produced a compiled geometry. Drives the colour indicator bar.
   */
  colorLineMap?: Map<number, [number, number, number, number]>;
  /**
   * 1-based line number of the currently hovered parameter in the sidebar.
   * A white tick is shown in the colour bar at this line while hovering.
   */
  hoveredParamLine?: number | null;
}

export const CodeEditor = forwardRef<CodeEditorRef, CodeEditorProps>(
  function CodeEditor(
    {
      value,
      onChange,
      onExtractToFile,
      onExtractModulesToFiles,
      onExplainWithAi,
      onImproveWithAi,
      onImproveParamsWithAi,
      language = 'openscad',
      filePath = 'input.scad',
      colorLineMap,
      hoveredParamLine = null,
    },
    ref,
  ) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const editorRef = useRef<any>(null);
    const monacoRef = useRef<Monaco | null>(null);
    const { resolvedTheme } = useTheme();

    const [contextFeature, setContextFeature] = useState<{
      feature: ScadFeature;
      replaceRange: any;
      initialValues?: Record<string, any>;
    } | null>(null);

    const [extractDialogOpen, setExtractDialogOpen] = useState(false);
    const [extractPayload, setExtractPayload] = useState<{
      selectedCode: string;
      fullCode: string;
      selectionRange: {
        startLineNumber: number;
        startColumn: number;
        endLineNumber: number;
        endColumn: number;
      };
    } | null>(null);

    const [extractVarDialogOpen, setExtractVarDialogOpen] = useState(false);
    const [extractVarPayload, setExtractVarPayload] = useState<{
      literal: string;
      detectedLiteral: DetectedLiteral;
      suggestedName: string;
      otherOccurrenceCount: number;
      existingVarNames: string[];
      selectionStartOffset: number;
      selectionEndOffset: number;
    } | null>(null);

    const [cursorPosition, setCursorPosition] = useState<{
      line: number;
      column: number;
    } | null>(null);

    const [showLineNumbers, setShowLineNumbers] = useState(false);
    const showLineNumbersRef = useRef(showLineNumbers);
    showLineNumbersRef.current = showLineNumbers;

    // State for the colour indicator bar — updated via Monaco event listeners.
    const [barScrollTop, setBarScrollTop] = useState(0);
    const [barLineHeight, setBarLineHeight] = useState(19); // Monaco default
    const [barContainerHeight, setBarContainerHeight] = useState(0);
    const [barTotalLines, setBarTotalLines] = useState(0);
    // Incremented on every model content change so CodeColorBar's useMemo
    // re-evaluates getLineTop() even when scroll/lineHeight didn't change.
    const [barContentVersion, setBarContentVersion] = useState(0);

    // Stable callback that delegates to the Monaco editor instance.
    // Wrapped in useCallback so the reference is stable across renders.
    const getLineTop = useCallback((lineNumber: number): number => {
      return editorRef.current?.getTopForLineNumber(lineNumber) ?? 0;
    }, []);

    useImperativeHandle(ref, () => ({
      editor: editorRef.current,
      monaco: monacoRef.current,
      undo: () => {
        editorRef.current?.trigger('source', 'undo', null);
      },
      redo: () => {
        editorRef.current?.trigger('source', 'redo', null);
      },
      setValueWithUndo: (newValue: string) => {
        const editor = editorRef.current;
        if (!editor) return;

        const model = editor.getModel();
        if (!model) return;

        const fullRange = model.getFullModelRange();
        editor.pushUndoStop();
        editor.executeEdits('source', [
          {
            range: fullRange,
            text: newValue,
          },
        ]);
        editor.pushUndoStop();
      },
      insertText: (text: string) => {
        const editor = editorRef.current;
        if (!editor) return;

        const position = editor.getPosition();
        editor.executeEdits('toolbar', [
          {
            range: {
              startLineNumber: position.lineNumber,
              startColumn: position.column,
              endLineNumber: position.lineNumber,
              endColumn: position.column,
            },
            text: text,
          },
        ]);
        editor.focus();
      },
      wrapSelection: (before: string, after: string) => {
        const editor = editorRef.current;
        if (!editor) return;

        const selection = editor.getSelection();
        const selectedText = editor.getModel().getValueInRange(selection);

        if (selectedText) {
          const newText = before + selectedText + after;
          editor.executeEdits('toolbar', [
            {
              range: selection,
              text: newText,
            },
          ]);
        } else {
          const position = editor.getPosition();
          editor.executeEdits('toolbar', [
            {
              range: {
                startLineNumber: position.lineNumber,
                startColumn: position.column,
                endLineNumber: position.lineNumber,
                endColumn: position.column,
              },
              text: before + after,
            },
          ]);
        }
        editor.focus();
      },
      getSelectedText: () => {
        const editor = editorRef.current;
        if (!editor) return null;

        const selection = editor.getSelection();
        if (selection.isEmpty()) return null;

        return editor.getModel().getValueInRange(selection);
      },
      hasSelection: () => {
        const editor = editorRef.current;
        if (!editor) return false;

        const selection = editor.getSelection();
        return !selection.isEmpty();
      },
      getValue: () => {
        const editor = editorRef.current;
        if (!editor) return '';
        return editor.getValue();
      },
      insertAtTop: (text: string) => {
        const editor = editorRef.current;
        if (!editor) return;

        const currentValue = editor.getValue();
        if (!currentValue.includes(text.trim())) {
          editor.executeEdits('toolbar', [
            {
              range: {
                startLineNumber: 1,
                startColumn: 1,
                endLineNumber: 1,
                endColumn: 1,
              },
              text: text + '\n',
            },
          ]);
        }
      },
      insertAtEnd: (text: string) => {
        const editor = editorRef.current;
        if (!editor) return;

        const model = editor.getModel();
        if (!model) return;

        const lineCount = model.getLineCount();
        const lastLineLength = model.getLineLength(lineCount);

        // Ensure we start on a fresh line
        const prefix = lastLineLength > 0 ? '\n' : '';

        editor.pushUndoStop();
        editor.executeEdits('toolbar', [
          {
            range: {
              startLineNumber: lineCount,
              startColumn: lastLineLength + 1,
              endLineNumber: lineCount,
              endColumn: lastLineLength + 1,
            },
            text: prefix + text,
          },
        ]);
        editor.pushUndoStop();
        // Scroll to the newly inserted text
        editor.revealLine(model.getLineCount());
      },
      replaceText: (search: string | RegExp, replacement: string) => {
        const editor = editorRef.current;
        if (!editor) return;
        const model = editor.getModel();
        if (!model) return;
        const full = model.getValue();
        const replaced = full.replace(search, replacement);
        if (replaced === full) return; // nothing matched
        const fullRange = model.getFullModelRange();
        editor.pushUndoStop();
        editor.executeEdits('replace', [{ range: fullRange, text: replaced }]);
        editor.pushUndoStop();
      },
    }));

    // Realtime syntax error checking via openscad-parser (only for SCAD files)
    useScadParser(
      language === 'openscad' ? editorRef.current : null,
      language === 'openscad' ? monacoRef.current : null,
      language === 'openscad' ? value : '',
    );

    // Define themes before Monaco mounts to ensure correct initial theme
    const handleEditorWillMount: BeforeMount = useCallback((monaco) => {
      // Define dark theme
      monaco.editor.defineTheme('playground-dark', {
        base: 'vs-dark',
        inherit: true,
        rules: [
          { token: 'comment', foreground: hex(DARK_COMMENT) },
          { token: 'keyword', foreground: hex(DARK_KEYWORD) },
          { token: 'identifier', foreground: hex(DARK_IDENTIFIER) },
          { token: 'string', foreground: hex(DARK_STRING) },
          { token: 'number', foreground: hex(DARK_NUMBER) },
          { token: 'delimiter', foreground: hex(DARK_DELIMITER) },
          { token: 'operator', foreground: hex(DARK_OPERATOR) },
          { token: 'bracket', foreground: hex(DARK_BRACKET) },
        ],
        colors: {
          'editor.background': DARK_BG,
          'editor.foreground': DARK_FG,
          'editor.lineHighlightBackground': DARK_LINE_HIGHLIGHT,
          'editorLineNumber.foreground': DARK_LINE_NUMBER,
          'editor.selectionBackground': DARK_SELECTION,
          'editor.inactiveSelectionBackground': DARK_SELECTION_INACTIVE,
          'editorBracketHighlight.foreground1': DARK_BRACKET_HIGHLIGHT,
          'editorBracketHighlight.foreground2': DARK_BRACKET_HIGHLIGHT,
          'editorBracketHighlight.foreground3': DARK_BRACKET_HIGHLIGHT,
          'editorBracketHighlight.foreground4': DARK_BRACKET_HIGHLIGHT,
          'editorBracketHighlight.foreground5': DARK_BRACKET_HIGHLIGHT,
          'editorBracketHighlight.foreground6': DARK_BRACKET_HIGHLIGHT,
        },
      });

      // Define light theme
      monaco.editor.defineTheme('playground-light', {
        base: 'vs',
        inherit: true,
        rules: [
          { token: 'comment', foreground: hex(LIGHT_COMMENT) },
          { token: 'keyword', foreground: hex(LIGHT_KEYWORD) },
          { token: 'identifier', foreground: hex(LIGHT_IDENTIFIER) },
          { token: 'string', foreground: hex(LIGHT_STRING) },
          { token: 'number', foreground: hex(LIGHT_NUMBER) },
          { token: 'delimiter', foreground: hex(LIGHT_DELIMITER) },
          { token: 'operator', foreground: hex(LIGHT_OPERATOR) },
        ],
        colors: {
          'editor.background': LIGHT_BG,
          'editor.foreground': LIGHT_FG,
          'editor.lineHighlightBackground': LIGHT_LINE_HIGHLIGHT,
          'editorLineNumber.foreground': LIGHT_LINE_NUMBER,
          'editor.selectionBackground': LIGHT_SELECTION,
          'editor.inactiveSelectionBackground': LIGHT_SELECTION_INACTIVE,
        },
      });
    }, []);

    // Keep refs to the latest callbacks so Monaco actions (registered once at
    // mount) always see the current handlers without needing re-registration.
    const onExtractToFileRef = useRef(onExtractToFile);
    onExtractToFileRef.current = onExtractToFile;

    const onExtractModulesToFilesRef = useRef(onExtractModulesToFiles);
    onExtractModulesToFilesRef.current = onExtractModulesToFiles;

    const onExplainWithAiRef = useRef(onExplainWithAi);
    onExplainWithAiRef.current = onExplainWithAi;

    const onImproveWithAiRef = useRef(onImproveWithAi);
    onImproveWithAiRef.current = onImproveWithAi;

    const onImproveParamsWithAiRef = useRef(onImproveParamsWithAi);
    onImproveParamsWithAiRef.current = onImproveParamsWithAi;

    const handleEditorDidMount: OnMount = useCallback((editor, monaco) => {
      editorRef.current = editor;
      monacoRef.current = monaco;

      // Define OpenSCAD language
      monaco.languages.register({ id: 'openscad' });

      // Register completion item provider
      monaco.languages.registerCompletionItemProvider(
        'openscad',
        getOpenSCADCompletionProvider(monaco),
      );

      monaco.languages.setMonarchTokensProvider('openscad', {
        brackets: [
          { open: '{', close: '}', token: 'delimiter.curly' },
          { open: '[', close: ']', token: 'delimiter.square' },
          { open: '(', close: ')', token: 'delimiter.parenthesis' },
          { open: '<', close: '>', token: 'delimiter.angle' },
        ],
        tokenizer: {
          root: [
            [/\/\*/, 'comment', '@comment'],
            [/\/\/.*$/, 'comment'],
            [
              /(module|function|if|else|for|intersection|union|difference|translate|rotate|scale|resize|mirror|multmatrix|color|material|material_def|minkowski|hull|cylinder|cube|sphere|circle|square|polygon|polyhedron|text|surface|import|echo|use|include|render|projection|linear_extrude|rotate_extrude|offset)(?=\()/,
              'keyword',
            ],
            [/(true|false|undef)/, 'keyword'],
            [
              /[a-zA-Z_$]\w*/,
              {
                cases: {
                  '@default': 'identifier',
                },
              },
            ],
            [/[{}()[\]]/, '@brackets'],
            [/[<>]/, '@brackets'],
            [/[;,.]/, 'delimiter'],
            [
              /@symbols/,
              { cases: { '@operators': 'operator', '@default': '' } },
            ],
            [/\d*\.\d+([eE][-+]?\d+)?/, 'number.float'],
            [/0[xX][0-9a-fA-F]+/, 'number.hex'],
            [/\d+/, 'number'],
            [/"([^"\\]|\\.)*$/, 'string.invalid'],
            [/"/, { token: 'string.quote', bracket: '@open', next: '@string' }],
          ],
          comment: [
            [/[^/*]+/, 'comment'],
            [/\*\//, 'comment', '@pop'],
            [/[/*]/, 'comment'],
          ],
          string: [
            [/[^\\"]+/, 'string'],
            [/\\./, 'string.escape'],
            [/"/, { token: 'string.quote', bracket: '@close', next: '@pop' }],
          ],
        },
        keywords: [
          'module',
          'function',
          'if',
          'else',
          'for',
          'intersection',
          'union',
          'difference',
          'translate',
          'rotate',
          'scale',
          'resize',
          'mirror',
          'multmatrix',
          'color',
          'material',
          'material_def',
          'minkowski',
          'hull',
          'cylinder',
          'cube',
          'sphere',
          'circle',
          'square',
          'polygon',
          'polyhedron',
          'text',
          'surface',
          'import',
          'echo',
          'use',
          'include',
          '$fn',
          '$fa',
          '$fs',
          'true',
          'false',
          'undef',
        ],
        operators: [
          '=',
          '>',
          '<',
          '!',
          '~',
          '?',
          ':',
          '==',
          '<=',
          '>=',
          '!=',
          '&&',
          '||',
          '++',
          '--',
          '+',
          '-',
          '*',
          '/',
          '&',
          '|',
          '^',
          '%',
          '<<',
          '>>',
          '>>>',
          '+=',
          '-=',
          '*=',
          '/=',
          '&=',
          '|=',
          '^=',
          '%=',
          '<<=',
          '>>=',
          '>>>=',
        ],
        symbols: /[=><!~?:&|+\-*/^%#]+/,
      });

      monaco.languages.setLanguageConfiguration('openscad', {
        comments: {
          lineComment: '//',
          blockComment: ['/*', '*/'],
        },
        brackets: [
          ['{', '}'],
          ['[', ']'],
          ['(', ')'],
        ],
        autoClosingPairs: [
          { open: '{', close: '}' },
          { open: '[', close: ']' },
          { open: '(', close: ')' },
          { open: '"', close: '"' },
        ],
        surroundingPairs: [
          { open: '{', close: '}' },
          { open: '[', close: ']' },
          { open: '(', close: ')' },
          { open: '"', close: '"' },
        ],
      });

      // ── Inline clickable decorations for editable feature invocations ───
      // We scan the document for known feature function calls that have a
      // dialog form, apply an `inlineClassName` decoration on the keyword, and
      // open the dialog when the user left-clicks that span.

      // Parallel array updated together with the decoration collection.
      // Each entry records exactly where a feature keyword lives so we can do
      // a fast O(n) hit-test in onMouseDown.
      interface ClickableEntry {
        range: import('monaco-editor').IRange;
        featureName: string;
        startColumn: number; // 1-based column of the keyword start (for handler)
      }
      let clickableEntries: ClickableEntry[] = [];

      const decorationCollection = editor.createDecorationsCollection();

      const scanAndDecorate = (model: import('monaco-editor').editor.ITextModel) => {
        const text = model.getValue();
        const newDecorations: import('monaco-editor').editor.IModelDeltaDecoration[] = [];
        const newEntries: ClickableEntry[] = [];
        const featureNames = Object.keys(ALL_FEATURES);
        const lineCount = model.getLineCount();

        for (let lineNumber = 1; lineNumber <= lineCount; lineNumber++) {
          const lineContent = model.getLineContent(lineNumber);
          if (!lineContent.includes('(')) continue;

          for (const name of featureNames) {
            let searchFrom = 0;
            while (searchFrom < lineContent.length) {
              const idx = lineContent.indexOf(name, searchFrom);
              if (idx === -1) break;
              searchFrom = idx + 1;

              // Whole-word match: not preceded by a word char, followed by '('
              const charBefore = idx > 0 ? lineContent[idx - 1] : ' ';
              const charAfter = lineContent[idx + name.length];
              if (/\w/.test(charBefore)) continue;
              if (charAfter !== '(') continue;

              // Verify a complete invocation exists
              const lineStart = model.getOffsetAt({ lineNumber, column: 1 });
              const absoluteOffset = lineStart + idx;
              if (!findFeatureInvocationFull(text, absoluteOffset, name)) continue;

              const startColumn = idx + 1; // 1-based
              const endColumn = idx + name.length + 1;
              const range = { startLineNumber: lineNumber, startColumn, endLineNumber: lineNumber, endColumn };

              newDecorations.push({
                range,
                options: {
                  inlineClassName: EDITABLE_CLASS,
                  hoverMessage: { value: `✎ Click to edit **${ALL_FEATURES[name].label}**` },
                  stickiness: 1, // NeverGrowsWhenTypingAtEdges
                },
              });
              newEntries.push({ range, featureName: name, startColumn });
            }
          }
        }

        decorationCollection.set(newDecorations);
        clickableEntries = newEntries;
      };

      // Initial scan once the model is available
      const initialModel = editor.getModel();
      if (initialModel) scanAndDecorate(initialModel);

      // Open the edit dialog when the user left-clicks a decorated keyword
      editor.onMouseDown((e) => {
        if (e.target.type !== 6 /* MouseTargetType.CONTENT_TEXT */) return;
        if (!e.event.leftButton) return;
        const pos = e.target.position;
        if (!pos) return;

        for (const entry of clickableEntries) {
          const r = entry.range;
          if (
            pos.lineNumber === r.startLineNumber &&
            pos.column >= r.startColumn &&
            pos.column <= r.endColumn
          ) {
            const model = editor.getModel();
            if (!model) break;

            const feature = ALL_FEATURES[entry.featureName];
            if (!feature?.fields?.length) break;

            const text = model.getValue();
            const offset = model.getOffsetAt({ lineNumber: r.startLineNumber, column: entry.startColumn });
            const invocation = findFeatureInvocationFull(text, offset, entry.featureName);

            if (!invocation) {
              setContextFeature({ feature, replaceRange: null, initialValues: {} });
            } else {
              const parsedValues = parseScadArgs(invocation.argsStr, feature.fields);
              const startPos = model.getPositionAt(invocation.startOffset);
              const endPos = model.getPositionAt(invocation.endOffset);
              setContextFeature({
                feature,
                replaceRange: {
                  startLineNumber: startPos.lineNumber,
                  startColumn: startPos.column,
                  endLineNumber: endPos.lineNumber,
                  endColumn: endPos.column,
                },
                initialValues: parsedValues,
              });
            }
            break;
          }
        }
      });

      // Add Edit Feature action
      editor.addAction({
        id: 'edit-scad-feature',
        label: 'Edit Feature...',
        contextMenuGroupId: 'navigation',
        contextMenuOrder: 1.1,
        precondition: 'editorTextFocus',
        run: (editor) => {
          const position = editor.getPosition();
          const model = editor.getModel();
          if (!position || !model) return;

          const word = model.getWordAtPosition(position);
          if (!word) return;

          const feature = ALL_FEATURES[word.word];
          if (!feature || !feature.fields || feature.fields.length === 0)
            return;

          const text = model.getValue();
          const offset = model.getOffsetAt({
            lineNumber: position.lineNumber,
            column: word.startColumn,
          });

          const invocation = findFeatureInvocationFull(text, offset, word.word);
          if (!invocation) {
            // Might be just the keyword without parents, we could still show dialog
            // but we wouldn't know the full range to replace easily.
            // Let's just fallback to replacing the word itself for now.
            const range = {
              startLineNumber: position.lineNumber,
              startColumn: word.startColumn,
              endLineNumber: position.lineNumber,
              endColumn: word.endColumn,
            };
            setContextFeature({
              feature,
              replaceRange: range,
              initialValues: {},
            });
            return;
          }

          const parsedValues = parseScadArgs(
            invocation.argsStr,
            feature.fields,
          );

          const startPos = model.getPositionAt(invocation.startOffset);
          const endPos = model.getPositionAt(invocation.endOffset);

          setContextFeature({
            feature,
            replaceRange: {
              startLineNumber: startPos.lineNumber,
              startColumn: startPos.column,
              endLineNumber: endPos.lineNumber,
              endColumn: endPos.column,
            },
            initialValues: parsedValues,
          });
        },
      });

      // Add context menu action for OpenSCAD Documentation
      editor.addAction({
        id: 'openscad-docs',
        label: 'OpenSCAD Documentation',
        contextMenuGroupId: 'navigation',
        contextMenuOrder: 1.5,
        run: (editor) => {
          const selection = editor.getSelection();
          const model = editor.getModel();
          if (selection && model && !selection.isEmpty()) {
            const text = model.getValueInRange(selection);
            window.open(
              `https://en.wikibooks.org/w/index.php?search=${encodeURIComponent(
                text,
              )}&title=Special%3ASearch&profile=default&fulltext=1`,
              '_blank',
            );
          } else {
            window.open('https://openscad.org/cheatsheet/', '_blank');
          }
        },
      });

      // Add context menu action for BOSL2 Documentation
      editor.addAction({
        id: 'bosl2-docs',
        label: 'BOSL2 Documentation',
        contextMenuGroupId: 'navigation',
        contextMenuOrder: 1.6,
        run: (editor) => {
          const selection = editor.getSelection();
          const model = editor.getModel();
          if (selection && model && !selection.isEmpty()) {
            const text = model.getValueInRange(selection);
            window.open(
              `https://github.com/BOSL2/BOSL2/search?q=${encodeURIComponent(
                text,
              )}&type=Wikis`,
              '_blank',
            );
          } else {
            window.open('https://github.com/BOSL2/BOSL2/wiki', '_blank');
          }
        },
      });

      // Add context menu action for MCAD Documentation
      editor.addAction({
        id: 'mcad-docs',
        label: 'MCAD Documentation',
        contextMenuGroupId: 'navigation',
        contextMenuOrder: 1.7,
        run: (editor) => {
          const selection = editor.getSelection();
          const model = editor.getModel();
          if (selection && model && !selection.isEmpty()) {
            const text = model.getValueInRange(selection);
            window.open(
              `https://github.com/search?q=repo%3Aopenscad%2FMCAD+${encodeURIComponent(
                text,
              )}&type=code`,
              '_blank',
            );
          } else {
            window.open('https://github.com/openscad/MCAD', '_blank');
          }
        },
      });

      // Add "Explain with AI" action — only visible when text is selected
      editor.addAction({
        id: 'explain-with-ai',
        label: 'Explain with AI',
        contextMenuGroupId: 'navigation',
        contextMenuOrder: 1.2,
        precondition: 'editorHasSelection',
        run: (editor) => {
          const selection = editor.getSelection();
          const model = editor.getModel();
          if (!selection || !model || selection.isEmpty()) return;
          const selectedCode = model.getValueInRange(selection);
          const contextCode = model.getValue();
          onExplainWithAiRef.current?.(selectedCode, contextCode);
        },
      });

      // Add "Improve with AI" action — only visible when text is selected
      editor.addAction({
        id: 'improve-with-ai',
        label: 'Improve with AI',
        contextMenuGroupId: 'navigation',
        contextMenuOrder: 1.25,
        precondition: 'editorHasSelection',
        run: (editor) => {
          const selection = editor.getSelection();
          const model = editor.getModel();
          if (!selection || !model || selection.isEmpty()) return;
          const selectedCode = model.getValueInRange(selection);
          const contextCode = model.getValue();
          onImproveWithAiRef.current?.(selectedCode, contextCode, {
            startLineNumber: selection.startLineNumber,
            startColumn: selection.startColumn,
            endLineNumber: selection.endLineNumber,
            endColumn: selection.endColumn,
          });
        },
      });

      // Add "Improve parameter config with AI" — always visible (whole-file action)
      editor.addAction({
        id: 'improve-params-with-ai',
        label: 'Improve parameter config with AI',
        contextMenuGroupId: 'navigation',
        contextMenuOrder: 1.26,
        run: (editor) => {
          const model = editor.getModel();
          if (!model) return;
          onImproveParamsWithAiRef.current?.(model.getValue());
        },
      });

      // Add "Extract to File" action — only visible when text is selected
      editor.addAction({
        id: 'extract-to-file',
        label: 'Extract to File...',
        contextMenuGroupId: 'navigation',
        contextMenuOrder: 1.3,
        precondition: 'editorHasSelection',
        run: (editor) => {
          const selection = editor.getSelection();
          const model = editor.getModel();
          if (!selection || !model || selection.isEmpty()) return;

          const selectedCode = model.getValueInRange(selection);
          const fullCode = model.getValue();

          setExtractPayload({
            selectedCode,
            fullCode,
            selectionRange: {
              startLineNumber: selection.startLineNumber,
              startColumn: selection.startColumn,
              endLineNumber: selection.endLineNumber,
              endColumn: selection.endColumn,
            },
          });
          setExtractDialogOpen(true);
        },
      });

      // Add "Extract to Variable" action — only when text is selected
      editor.addAction({
        id: 'extract-to-variable',
        label: 'Extract to Variable...',
        contextMenuGroupId: 'navigation',
        contextMenuOrder: 1.35,
        precondition: 'editorHasSelection',
        run: (editor) => {
          const selection = editor.getSelection();
          const model = editor.getModel();
          if (!selection || !model || selection.isEmpty()) return;

          const literal = model.getValueInRange(selection).trim();
          const detected = detectLiteralType(literal);
          if (detected.type === 'unknown') return;

          const fullCode = model.getValue();
          const selectionStartOffset = model.getOffsetAt({
            lineNumber: selection.startLineNumber,
            column: selection.startColumn,
          });
          const selectionEndOffset = model.getOffsetAt({
            lineNumber: selection.endLineNumber,
            column: selection.endColumn,
          });

          const suggestedName = suggestVariableNameForLiteral(
            fullCode,
            selectionStartOffset,
            literal,
          );
          const otherCount = countOtherLiteralOccurrences(
            fullCode,
            literal,
            selectionStartOffset,
          );
          // parseOuterVariables with impossibly large exclude range to get all names
          const existingVars = parseOuterVariables(fullCode, -1, -1);

          setExtractVarPayload({
            literal,
            detectedLiteral: detected,
            suggestedName,
            otherOccurrenceCount: otherCount,
            existingVarNames: existingVars,
            selectionStartOffset,
            selectionEndOffset,
          });
          setExtractVarDialogOpen(true);
        },
      });

      // Add "Extract Modules to Files" action — visible when nothing is selected
      editor.addAction({
        id: 'extract-modules-to-files',
        label: 'Extract Modules to Files',
        contextMenuGroupId: 'navigation',
        contextMenuOrder: 1.4,
        precondition: '!editorHasSelection',
        run: () => {
          onExtractModulesToFilesRef.current?.();
        },
      });

      // Add "Copy All" action — copies the entire editor content to clipboard
      editor.addAction({
        id: 'copy-all',
        label: 'Copy All',
        contextMenuGroupId: '9_cutcopypaste',
        contextMenuOrder: 3.1,
        run: (editor) => {
          const model = editor.getModel();
          if (!model) return;
          navigator.clipboard.writeText(model.getValue());
        },
      });

      // Add "Copy All (Pure OpenSCAD)" action — copies with Meshpit features stripped
      editor.addAction({
        id: 'copy-all-pure-openscad',
        label: 'Copy All (Pure OpenSCAD)',
        contextMenuGroupId: '9_cutcopypaste',
        contextMenuOrder: 3.2,
        run: (editor) => {
          const model = editor.getModel();
          if (!model) return;
          const stripped = stripMeshpitCode(model.getValue());
          navigator.clipboard.writeText(stripped);
        },
      });

      // Add "Toggle Line Numbers" action
      editor.addAction({
        id: 'toggle-line-numbers',
        label: 'Toggle Line Numbers',
        contextMenuGroupId: 'navigation',
        contextMenuOrder: 1.9,
        run: (editor) => {
          const next = !showLineNumbersRef.current;
          showLineNumbersRef.current = next;
          setShowLineNumbers(next);
          editor.updateOptions({
            lineNumbers: next ? 'on' : 'off',
            lineDecorationsWidth: next ? 8 : 12,
          });
        },
      });

      // Track cursor position for the status overlay
      const pos = editor.getPosition();
      if (pos) setCursorPosition({ line: pos.lineNumber, column: pos.column });

      editor.onDidChangeCursorPosition((e) => {
        setCursorPosition({
          line: e.position.lineNumber,
          column: e.position.column,
        });
      });

      editor.onDidBlurEditorText(() => {
        setCursorPosition(null);
      });

      editor.onDidFocusEditorText(() => {
        const p = editor.getPosition();
        if (p) setCursorPosition({ line: p.lineNumber, column: p.column });
      });

      // ── Colour indicator bar: track scroll + layout ──────────────────────
      const syncBarMetrics = () => {
        setBarScrollTop(editor.getScrollTop());

        const layout = editor.getLayoutInfo();
        if (layout.height > 0) setBarContainerHeight(layout.height);

        // Derive the actual rendered line height by measuring the pixel distance
        // between the tops of adjacent lines. This is more reliable than
        // EditorOption.lineHeight which returns 0 when not explicitly configured.
        const model = editor.getModel();
        if (model) {
          const lineCount = model.getLineCount();
          setBarTotalLines(lineCount);

          if (lineCount >= 2) {
            // Measure between lines 1 and 2 for the most stable result.
            const top1 = editor.getTopForLineNumber(1);
            const top2 = editor.getTopForLineNumber(2);
            const measured = top2 - top1;
            if (measured > 0) setBarLineHeight(measured);
          } else if (lineCount === 1) {
            // Single-line fallback: use scrollHeight minus padding.
            const scrollH = editor.getScrollHeight();
            const paddingT = 12;
            const paddingB = 12;
            const h = scrollH - paddingT - paddingB;
            if (h > 0) setBarLineHeight(h);
          }
        }
      };

      // Defer first sync one frame so Monaco has finished layout
      requestAnimationFrame(syncBarMetrics);
      editor.onDidScrollChange(() => {
        setBarScrollTop(editor.getScrollTop());
      });
      editor.onDidLayoutChange(() => {
        syncBarMetrics();
      });
      editor.onDidChangeModelContent(() => {
        syncBarMetrics();
        setBarContentVersion((v) => v + 1);
        const model = editor.getModel();
        if (model) scanAndDecorate(model);
      });
    }, []);

    const themeName =
      resolvedTheme === 'dark' ? 'playground-dark' : 'playground-light';

    const editorOptions = useMemo(
      () => ({
        minimap: { enabled: false },
        fontSize: 13,
        fontFamily: 'JetBrains Mono, Menlo, Monaco, monospace',
        lineNumbers: (showLineNumbers ? 'on' : 'off') as 'on' | 'off',
        scrollBeyondLastLine: false,
        automaticLayout: true,
        tabSize: 2,
        wordWrap: 'on' as const,
        padding: { top: 12, bottom: 12 },
        folding: false,
        glyphMargin: false,
        lineDecorationsWidth: showLineNumbers ? 8 : 12,
        lineNumbersMinChars: 0,
        renderLineHighlight: 'none' as const,
      }),
      [showLineNumbers],
    );

    return (
      <>
        <div className="relative h-full w-full">
          <Editor
            height="100%"
            defaultLanguage={language}
            language={language}
            path={filePath}
            keepCurrentModel={true}
            value={value}
            onChange={onChange}
            beforeMount={handleEditorWillMount}
            onMount={handleEditorDidMount}
            theme={themeName}
            options={editorOptions}
          />
          {!value?.trim() && (
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-3 select-none">
              <Code2 className="h-8 w-8 text-muted-foreground/30" strokeWidth={1.25} />
              <div className="flex flex-col items-center gap-1 text-center">
                <p className="text-sm font-medium text-muted-foreground/60">
                  Start writing OpenSCAD code
                </p>
                <p className="text-xs text-muted-foreground/40">
                  Use the toolbar above to insert shapes, or type directly
                </p>
              </div>
              <div className="pointer-events-auto mt-1 flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() =>
                    onChange(
                      '// @param\nsize = 20; // [5:100]\n\ncube([size, size, size]);',
                    )
                  }
                >
                  Add example
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-muted-foreground"
                  onClick={() =>
                    window.open('/playground/blog/meshpit-api-reference', '_blank')
                  }
                >
                  <ExternalLink className="mr-1.5 h-3 w-3" />
                  API reference
                </Button>
              </div>
            </div>
          )}
          {cursorPosition && (
            <div className="pointer-events-none absolute bottom-2 right-3 select-none font-mono text-[11px] text-foreground/40">
              {cursorPosition.line}:{cursorPosition.column}
            </div>
          )}
          {colorLineMap && (
            <CodeColorBar
              colorLineMap={colorLineMap}
              hoveredParamLine={hoveredParamLine ?? null}
              getLineTop={getLineTop}
              lineHeight={barLineHeight}
              scrollTop={barScrollTop}
              containerHeight={barContainerHeight}
              paddingTop={12}
              contentVersion={barContentVersion}
            />
          )}
        </div>
        <ExtractToFileDialog
          open={extractDialogOpen}
          onClose={() => {
            setExtractDialogOpen(false);
            setExtractPayload(null);
          }}
          onConfirm={(payload) => {
            onExtractToFileRef.current?.(payload);
          }}
          selectedCode={extractPayload?.selectedCode ?? ''}
          fullCode={extractPayload?.fullCode ?? ''}
          selectionRange={extractPayload?.selectionRange ?? null}
        />
        <ExtractToVariableDialog
          open={extractVarDialogOpen}
          onClose={() => {
            setExtractVarDialogOpen(false);
            setExtractVarPayload(null);
          }}
          onConfirm={(result: ExtractToVariableResult) => {
            if (!extractVarPayload) return;
            const model = editorRef.current?.getModel();
            if (!model) return;
            const fullCode = model.getValue();
            const insertionLine = findInsertionLineForVariable(fullCode);
            const newCode = applyExtractToVariable(fullCode, {
              literal: extractVarPayload.literal,
              varName: result.varName,
              insertionLine,
              replaceAll: result.replaceAll,
              selectionStartOffset: extractVarPayload.selectionStartOffset,
              selectionEndOffset: extractVarPayload.selectionEndOffset,
              annotation: result.makeParameter ? result.annotation : null,
              tooltip:
                result.makeParameter && result.tooltip ? result.tooltip : null,
            });
            // Apply as a single undoable edit
            const fullRange = model.getFullModelRange();
            editorRef.current?.pushUndoStop();
            editorRef.current?.executeEdits('extract-to-variable', [
              { range: fullRange, text: newCode },
            ]);
            editorRef.current?.pushUndoStop();
          }}
          literal={extractVarPayload?.literal ?? ''}
          detectedLiteral={
            extractVarPayload?.detectedLiteral ?? { type: 'unknown' }
          }
          suggestedName={extractVarPayload?.suggestedName ?? 'value'}
          otherOccurrenceCount={extractVarPayload?.otherOccurrenceCount ?? 0}
          existingVarNames={extractVarPayload?.existingVarNames ?? []}
        />
        <ScadFeatureDialog
          feature={contextFeature?.feature || null}
          initialValues={contextFeature?.initialValues}
          isEditing={
            contextFeature !== null &&
            contextFeature.replaceRange !== null &&
            Object.keys(contextFeature.initialValues ?? {}).length > 0
          }
          isOpen={contextFeature !== null}
          onClose={() => setContextFeature(null)}
          onSubmit={(result) => {
            if (contextFeature && editorRef.current) {
              const editor = editorRef.current;
              // Add library import if needed
              if (contextFeature.feature.library) {
                const libName = contextFeature.feature.library;
                const currentCode = editor.getValue();
                let importCode = '';
                if (libName === 'BOSL2')
                  importCode = 'include <BOSL2/std.scad>\n';
                else if (libName === 'MCAD')
                  importCode = 'include <MCAD/units.scad>\n';
                else if (libName === 'LEGO')
                  importCode = 'use <LEGO/lego.scad>\n';
                else if (libName === 'BOSL')
                  importCode =
                    'include <BOSL/constants.scad>\nuse <BOSL/shapes.scad>\n';
                else if (libName === 'gridfinity')
                  importCode =
                    'include <gridfinity/gridfinity-rebuilt-bins.scad>\n';
                else importCode = `include <${libName}/std.scad>\n`;

                if (!currentCode.includes(`<${libName}/`)) {
                  editor.executeEdits('context-menu', [
                    {
                      range: {
                        startLineNumber: 1,
                        startColumn: 1,
                        endLineNumber: 1,
                        endColumn: 1,
                      },
                      text: importCode,
                    },
                  ]);
                }
              }

              // Replace code
              let replacementText = '';
              if (typeof result === 'string') {
                // For standalone features
                replacementText = result.trim();
              } else {
                // For wrap features, if we couldn't parse the inside, maybe we just wrap?
                // Usually if they edit it via context menu, it's already wrapping something.
                // If we had parsed it successfully, we probably don't want to double wrap.
                // Actually, if it's a wrapper like translate, the `replaceRange` covers the `translate([x,y,z])` part.
                // Wait, `findFeatureInvocationFull` only matches `translate(...)`.
                // So we should replace `translate(...)` with `result.before.trim()`.
                // e.g. `result.before` is `translate([10, 0, 0])
                // We trim the newline.
                replacementText = result.before.trim();
              }

              if (contextFeature.replaceRange) {
                editor.executeEdits('context-menu', [
                  {
                    range: contextFeature.replaceRange,
                    text: replacementText,
                  },
                ]);
              } else {
                // No range to replace — insert at cursor position
                const position = editor.getPosition();
                if (position) {
                  editor.executeEdits('context-menu', [
                    {
                      range: {
                        startLineNumber: position.lineNumber,
                        startColumn: position.column,
                        endLineNumber: position.lineNumber,
                        endColumn: position.column,
                      },
                      text: replacementText,
                    },
                  ]);
                }
              }
            }
            setContextFeature(null);
          }}
        />
      </>
    );
  },
);
